import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import Layout from "../layout/Layout";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import { Button, Progress } from "reactstrap";
import queryString from "query-string";
import "../styles/VideoEditor.scss";

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const totalSeconds = Math.floor(seconds);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const padded = secs < 10 ? `0${secs}` : `${secs}`;
  return `${mins}:${padded}`;
}

export default function VideoEditor() {
  const intl = useIntl();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  type Segment = { id: string; start: number; end: number };
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null
  );

  const { src: rawSrc } = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );

  const videoSrc =
    typeof rawSrc === "string" && rawSrc.length > 0
      ? rawSrc
      : "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";

  const handleLoadedMetadata = useCallback(() => {
    if (!videoRef.current) return;
    const videoDuration = videoRef.current.duration || 0;
    setDuration(videoDuration);
    setTrimStart(0);
    setTrimEnd(videoDuration);
    setSegments([{ id: "segment-0", start: 0, end: videoDuration }]);
    setSelectedSegmentId("segment-0");
    videoRef.current.volume = volume;
    videoRef.current.muted = isMuted;
  }, [isMuted, volume]);

  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime || 0);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleSeek = useCallback(
    (value: number) => {
      if (!videoRef.current) return;
      const clamped = Math.min(Math.max(value, 0), duration || 0);
      videoRef.current.currentTime = clamped;
      setCurrentTime(clamped);
    },
    [duration]
  );

  const handleTrimStartChange = useCallback(
    (value: number) => {
      const safeValue = Math.max(0, Math.min(value, trimEnd));
      setTrimStart(safeValue);
      if (videoRef.current) {
        videoRef.current.currentTime = safeValue;
      }
    },
    [trimEnd]
  );

  const handleTrimEndChange = useCallback(
    (value: number) => {
      const max = duration || 0;
      const safeValue = Math.min(max, Math.max(value, trimStart));
      setTrimEnd(safeValue);
    },
    [duration, trimStart]
  );

  const handlePreviewTrim = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = trimStart;
    setCurrentTime(trimStart);
    videoRef.current.play();
    setIsPlaying(true);
  }, [trimStart]);

  const handleExport = useCallback(() => {
    // De momento solo mostramos en consola la “receta” de edición
    // para luego conectarlo con un backend de render.
    // eslint-disable-next-line no-console
    console.log("Video editor export", {
      src: videoSrc,
      trimStart,
      trimEnd,
    });
    alert(
      intl.formatMessage({
        id: "app.videoEditor.exportMock",
        defaultMessage:
          "Se simula la exportación del video con los cortes seleccionados.",
      })
    );
  }, [intl, trimEnd, trimStart, videoSrc]);

  const progressForCurrent = useMemo(() => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  }, [currentTime, duration]);

  const progressForTrim = useMemo(() => {
    if (!duration) return 0;
    return ((trimEnd - trimStart) / duration) * 100;
  }, [duration, trimEnd, trimStart]);

  const handleVolumeChange = useCallback(
    (value: number) => {
      const next = Math.min(Math.max(value, 0), 1);
      setVolume(next);
      if (videoRef.current) {
        videoRef.current.volume = next;
        if (next > 0) {
          videoRef.current.muted = false;
          setIsMuted(false);
        }
      }
    },
    []
  );

  const handleToggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    videoRef.current.muted = nextMuted;
  }, [isMuted]);

  const handleSplitSegment = useCallback(() => {
    if (!duration || !segments.length) return;
    const time = currentTime;
    const baseSegment =
      segments.find((s) => time >= s.start && time <= s.end) ?? segments[0];
    if (!baseSegment) return;
    if (time <= baseSegment.start + 0.1 || time >= baseSegment.end - 0.1) {
      return;
    }
    const first: Segment = {
      id: `${baseSegment.id}-a-${time.toFixed(2)}`,
      start: baseSegment.start,
      end: time,
    };
    const second: Segment = {
      id: `${baseSegment.id}-b-${time.toFixed(2)}`,
      start: time,
      end: baseSegment.end,
    };
    const nextSegments = segments
      .flatMap((s) => (s.id === baseSegment.id ? [first, second] : [s]))
      .sort((a, b) => a.start - b.start);
    setSegments(nextSegments);
    setSelectedSegmentId(second.id);
  }, [currentTime, duration, segments]);

  const handleDeleteSegment = useCallback(() => {
    if (!segments.length || !selectedSegmentId) return;
    if (segments.length === 1) return;
    const nextSegments = segments.filter((s) => s.id !== selectedSegmentId);
    nextSegments.sort((a, b) => a.start - b.start);
    setSegments(nextSegments);
    setSelectedSegmentId(nextSegments[0]?.id ?? null);
  }, [segments, selectedSegmentId]);

  const totalDurationForTrack = duration || 1;

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.sidebar.ia.videoEditor" })}
      />
      <Content className="video-editor-content">
        <div className="video-editor-layout">
          <section className="video-editor-player">
            <div className="video-editor-player__video-wrapper">
              <video
                ref={videoRef}
                src={videoSrc}
                controls={false}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                className="video-editor-player__video"
              />
            </div>
            <div className="video-editor-player__controls">
              <Button color="primary" size="sm" onClick={handlePlayPause}>
                {isPlaying
                  ? intl.formatMessage({ id: "app.videoEditor.pause" })
                  : intl.formatMessage({ id: "app.videoEditor.play" })}
              </Button>
              <div className="video-editor-player__time">
                <span>{formatTime(currentTime)}</span>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={(e) => handleSeek(Number(e.target.value))}
                  className="video-editor-player__seek"
                />
                <span>{formatTime(duration)}</span>
              </div>
              <div className="video-editor-player__audio">
                <Button
                  color="link"
                  size="sm"
                  onClick={handleToggleMute}
                  className="p-0 mr-2"
                >
                  {isMuted
                    ? intl.formatMessage({ id: "app.videoEditor.unmute" })
                    : intl.formatMessage({ id: "app.videoEditor.mute" })}
                </Button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => handleVolumeChange(Number(e.target.value))}
                  className="video-editor-player__volume"
                />
              </div>
            </div>
          </section>

          <section className="video-editor-timeline">
            <h5 className="video-editor-timeline__title">
              {intl.formatMessage({ id: "app.videoEditor.timeline" })}
            </h5>

            <div className="video-editor-timeline__toolbar">
              <Button color="link" size="sm" disabled>
                {intl.formatMessage({ id: "app.videoEditor.insert", defaultMessage: "Insertar" })}
              </Button>
              <Button
                color="link"
                size="sm"
                onClick={handleSplitSegment}
                disabled={!duration}
              >
                {intl.formatMessage({
                  id: "app.videoEditor.split",
                  defaultMessage: "Dividir",
                })}
              </Button>
              <Button
                color="link"
                size="sm"
                onClick={handleDeleteSegment}
                disabled={!duration || segments.length <= 1}
              >
                {intl.formatMessage({
                  id: "app.videoEditor.delete",
                  defaultMessage: "Eliminar",
                })}
              </Button>
            </div>

            <div className="video-editor-timeline__track">
              <div className="video-editor-timeline__wave" />
              <div className="video-editor-timeline__segments">
                {segments.map((segment) => {
                  const widthPercent =
                    ((segment.end - segment.start) / totalDurationForTrack) *
                    100;
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      className={
                        "video-editor-timeline__segment" +
                        (segment.id === selectedSegmentId
                          ? " video-editor-timeline__segment--selected"
                          : "")
                      }
                      style={{ width: `${widthPercent}%` }}
                      onClick={() => setSelectedSegmentId(segment.id)}
                    />
                  );
                })}
              </div>
              <div
                className="video-editor-timeline__playhead"
                style={{
                  left: `${(currentTime / totalDurationForTrack) * 100}%`,
                }}
              />
            </div>

            <div className="video-editor-timeline__trim">
              <div className="video-editor-timeline__labels">
                <span>
                  {intl.formatMessage({ id: "app.videoEditor.trimStart" })}:{" "}
                  <strong>{formatTime(trimStart)}</strong>
                </span>
                <span>
                  {intl.formatMessage({ id: "app.videoEditor.trimEnd" })}:{" "}
                  <strong>{formatTime(trimEnd)}</strong>
                </span>
              </div>

              <div className="video-editor-timeline__sliders">
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={trimStart}
                  onChange={(e) =>
                    handleTrimStartChange(Number(e.target.value))
                  }
                  className="video-editor-timeline__slider video-editor-timeline__slider--start"
                />
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={trimEnd}
                  onChange={(e) =>
                    handleTrimEndChange(Number(e.target.value))
                  }
                  className="video-editor-timeline__slider video-editor-timeline__slider--end"
                />
              </div>

              <div className="video-editor-timeline__bar-wrapper">
                <Progress
                  value={100}
                  className="video-editor-timeline__bar video-editor-timeline__bar--background"
                />
                <Progress
                  value={progressForTrim}
                  className="video-editor-timeline__bar video-editor-timeline__bar--selection"
                />
              </div>

              <div className="video-editor-timeline__actions">
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  onClick={handlePreviewTrim}
                  disabled={!duration}
                >
                  {intl.formatMessage({ id: "app.videoEditor.previewTrim" })}
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={!duration || trimEnd <= trimStart}
                >
                  {intl.formatMessage({ id: "app.videoEditor.export" })}
                </Button>
              </div>
            </div>

            <div className="video-editor-timeline__hint text-muted mt-2">
              {intl.formatMessage({ id: "app.videoEditor.hint" })}
            </div>
          </section>
        </div>
      </Content>
    </Layout>
  );
}
